import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, HelpCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { QuestionsFlow } from '@/components/questions/QuestionsFlow';
import { DPEAPI } from '@/services/api';

export const QuestionsPage: React.FC = () => {
  const { id: dpeId, questionnaireId: questionnaireIdFromUrl } = useParams<{ id: string; questionnaireId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvedQuestionnaireId, setResolvedQuestionnaireId] = useState<string | undefined>(questionnaireIdFromUrl);

  React.useEffect(() => {
    if (!dpeId) {
      setError('ID DPE manquant');
      setLoading(false);
      return;
    }

    if (questionnaireIdFromUrl) {
      setResolvedQuestionnaireId(questionnaireIdFromUrl);
      setLoading(false);
      return;
    }

    // Pas d'ID dans l'URL : on cree/recupere une session via l'API puis on redirige
    DPEAPI.getQuestions(dpeId)
      .then((response) => {
        if (response.success && response.session_id) {
          navigate(`/analysis/${dpeId}/questions/${response.session_id}`, { replace: true });
        } else {
          setError('Impossible de charger les questions. Veuillez réessayer.');
          setLoading(false);
        }
      })
      .catch(() => {
        setError('Impossible de charger les questions. Veuillez réessayer.');
        setLoading(false);
      });
  }, [dpeId, questionnaireIdFromUrl]);

  // Gérer la completion du questionnaire
  const handleQuestionsComplete = (recommendations: any) => {
    console.log('Questions terminées, recommandations reçues:', recommendations);
    navigate(`/analysis/${dpeId}/results`, { replace: true });
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <Button
            variant="ghost"
            size="sm"
            className="mb-4"
            onClick={() => navigate(`/analysis/${dpeId}`)}
            icon={<ArrowLeft className="w-4 h-4 mr-2" />}
          >
            Retour à l'analyse
          </Button>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Questions contextuelles
          </h1>
          <p className="text-gray-600">
            Chargement des questions personnalisées...
          </p>
        </div>

        <div className="flex flex-col items-center justify-center py-12">
          <LoadingSpinner size="lg" text="Chargement des questions contextuelles..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <Button
            variant="ghost"
            size="sm"
            className="mb-4"
            onClick={() => navigate(`/analysis/${dpeId}`)}
            icon={<ArrowLeft className="w-4 h-4 mr-2" />}
          >
            Retour à l'analyse
          </Button>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Erreur
          </h1>
        </div>

        <Card>
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Erreur de chargement
            </h3>
            <p className="text-gray-600 mb-6">
              {error}
            </p>
            <Button
              variant="primary"
              onClick={() => navigate(`/analysis/${dpeId}`)}
            >
              Retour à l'analyse
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <Helmet>
        <title>Questions contextuelles - DPEWizard</title>
        <meta name="description" content="Répondez aux questions contextuelles pour affiner votre analyse DPE et obtenir des recommandations personnalisées." />
      </Helmet>

      <div className="mb-8">
        <Button
          variant="ghost"
          size="sm"
          className="mb-4"
          onClick={() => navigate(`/analysis/${dpeId}`)}
          icon={<ArrowLeft className="w-4 h-4 mr-2" />}
        >
          Retour à l'analyse
        </Button>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Questions contextuelles
        </h1>
        <p className="text-gray-600">
          Répondez à ces questions pour affiner l'analyse et obtenir des recommandations personnalisées
        </p>
      </div>

      {/* Help card */}
      <Card className="mb-8 bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start">
            <HelpCircle className="w-5 h-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-blue-800 mb-1">
                Pourquoi ces questions ?
              </h3>
              <p className="text-sm text-blue-700">
                Ces questions sont générées spécifiquement pour votre DPE en fonction des erreurs détectées et des opportunités identifiées. Vos réponses permettront d'affiner l'analyse et de générer des recommandations personnalisées.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Questions Flow Component */}
      <QuestionsFlow
        dpeId={dpeId!}
        questionnaireId={resolvedQuestionnaireId}
        onComplete={handleQuestionsComplete}
      />
    </div>
  );
};

export default QuestionsPage;
